import SearchSelect from "../searchSelect/SearchSelect";
import { findObjectUseCase } from "../../usecases/object";
import BaseFormPage from "../../base/BaseFormPage";

export default class Hierarchy extends BaseFormPage {
  constructor(props) {
    super(props);
    this.state = {
      object: {},
      advanced: false,
      region: [],
      office: [],
      division: [],
      section: [],
      unit: [],
      cluster: [],
      selectedRegion: null,
      selectedOffice: null,
      selectedDivision: null,
      selectedSection: null,
      selectedUnit: null,
      selectedCluster: null,
    };
  }

  getCollectionName() {
    return "Clusters";
  }

  async componentDidMount() {
    const region = await findObjectUseCase().execute("Region", {
      where: { status: "active" },
    });
    this.setState({ region });
    this.loadRegion();
  }

  componentDidUpdate(prevProps) {
    const prevObjectId = prevProps.object?.id;
    const objectId = this.props.object?.id;

    if (prevObjectId !== objectId && objectId) {
      this.loadRegion();
    }
  }

  async loadRegion() {
    const { object } = this.props;
    if (object?.region) {
      const query = {
        where: {
          status: "active",
        },
      };
      const region = await findObjectUseCase().execute("Region", query);
      const selected = region.find((item) => item.id === object.region);
      this.setState({
        region,
        selectedRegion: { id: selected?.id, name: selected?.name },
      });
      this.loadOfficeData(object, query);
    }
  }

  async loadOfficeData(object, query) {
    const officeQuery = {
      where: {
        ...query.where,
        region: object.region,
      },
    };
    if (object.region) {
      const office = await findObjectUseCase().execute("offices", officeQuery);
      const selected = office.find((item) => item.id === object.office);
      this.setState({
        office,
        selectedOffice: { name: selected?.name, id: selected?.id },
      });
      this.loadDivisionData(object, query);
    }
  }

  async loadDivisionData(object, query) {
    const divisionQuery = {
      where: {
        ...query.where,
        office: object.office,
      },
    };
    if (object.office) {
      const division = await findObjectUseCase().execute(
        "Division",
        divisionQuery,
      );
      const selected = division.find((item) => item.id === object.division);
      this.setState({
        division,
        selectedDivision: { name: selected?.name, id: selected?.id },
      });
    }
    this.loadSectionData(object, query);
  }

  async loadSectionData(object, query) {
    if (object.section) {
      const sectionQuery = {
        where: {
          ...query.where,
          division: object.division,
        },
      };
      const section = await findObjectUseCase().execute(
        "Sections",
        sectionQuery,
      );
      const selected = section.find((item) => item.id === object.section);
      this.setState({
        section,
        selectedSection: { name: selected?.name, id: selected?.id },
      });
      this.loadUnitData(object, query);
    }
  }

  async loadUnitData(object, query) {
    if (object.unit) {
      const unitQuery = {
        where: {
          ...query.where,
          section: object.section,
        },
      };
      const unit = await findObjectUseCase().execute("Units", unitQuery);
      const selected = unit.find((item) => item.id === object.unit);
      this.setState({
        unit,
        selectedUnit: { id: selected?.id, name: selected?.name },
      });
      this.loadClusterData(object, query);
    }
  }
  async loadClusterData(object, query) {
    if (object.cluster) {
      const unitQuery = {
        where: {
          ...query.where,
          unit: object.unit,
        },
      };
      const cluster = await findObjectUseCase().execute("Clusters", unitQuery);
      const selected = cluster.find((item) => item.id === object.cluster);
      this.setState({
        cluster,
        selectedCluster: { id: selected?.id, name: selected?.name },
      });
    }
  }

  passToParent() {
    const {
      selectedRegion,
      selectedOffice,
      selectedDivision,
      selectedSection,
      selectedUnit,
      selectedCluster,
    } = this.state;

    this.props.handleObjectChange({
      region: selectedRegion?.id || "",
      office: selectedOffice?.id || "",
      division: selectedDivision?.id || "",
      section: selectedSection?.id || "",
      unit: selectedUnit?.id || "",
      cluster: selectedCluster?.id || "",
    });
  }
  // Separate method for handling clear action
  handleHierarchyClear(field) {
    const clearMap = {
      Region: {
        clear: ["office", "division", "section", "unit", "cluster"],
        clearSelection: [
          "selectedRegion",
          "selectedOffice",
          "selectedDivision",
          "selectedSection",
          "selectedUnit",
          "selectedCluster",
        ],
      },
      Office: {
        clear: ["division", "section", "unit", "cluster"],
        clearSelection: [
          "selectedOffice",
          "selectedDivision",
          "selectedSection",
          "selectedUnit",
          "selectedCluster",
        ],
      },
      Division: {
        clear: ["section", "unit", "cluster"],
        clearSelection: [
          "selectedDivision",
          "selectedSection",
          "selectedUnit",
          "selectedCluster",
        ],
      },
      Section: {
        clear: ["unit", "cluster"],
        clearSelection: ["selectedSection", "selectedUnit", "selectedCluster"],
      },
      Unit: {
        clear: ["cluster"],
        clearSelection: ["selectedUnit", "selectedCluster"],
      },
      Cluster: {
        clear: [],
        clearSelection: ["selectedCluster"],
      },
    };

    if (clearMap[field]) {
      const { clear, clearSelection } = clearMap[field];
      this.setState(
        {
          ...Object.fromEntries(clear.map((state) => [state, []])),
          ...Object.fromEntries(clearSelection.map((sel) => [sel, null])),
        },
        () => this.passToParent(),
      );
    }
  }

  async handleHierarchyChange(field, value) {
    const selectionField = `selected${field}`;

    const fieldMap = {
      Region: {
        endpoint: "offices",
        params: { region: value.id || ":" },
        newState: "office",
      },
      Office: {
        endpoint: "Division",
        params: { office: value.id || ":" },
        newState: "division",
      },
      Division: {
        endpoint: "Sections",
        params: { division: value.id || ":" },
        newState: "section",
      },
      Section: {
        endpoint: "Units",
        params: { section: value.id || ":" },
        newState: "unit",
      },
      Unit: {
        endpoint: "Clusters",
        params: { unit: value.id || ":" },
        newState: "cluster",
      },
      Cluster: {
        endpoint: "Clusters", // May be optional if Cluster is final
        params: { id: value.id || ":" },
        newState: "",
      },
    };

    if (fieldMap[field]) {
      const { endpoint, params, newState } = fieldMap[field];

      // Clear dependent fields before setting new values
      this.handleHierarchyClear(field);

      try {
        this.setState({ [selectionField]: value });

        // Fetch the updated data
        const result = await findObjectUseCase().execute(endpoint, {
          where: { status: "active", ...params },
        });

        if (newState) {
          this.setState({ [newState]: result }, () => this.passToParent());
        } else {
          this.passToParent(); // If no more levels
        }
      } catch (e) {
        console.error(e);
        this.showError(e.message || e);
      }
    }
  }

  render() {
    const { className, disabled, handleObjectChange, required, ...props } =
      this.props;
    // const schema = this.getSchema(this.getCollectionName());
    // if (!schema) return <h1>no schema</h1>;
    const fieldLabel = {
      Region: "OCOM",
      Office: "Location",
      Division: "Region",
      Section: "Subport/ IPEE/ Main RIOU",
      Unit: "Division",
      Cluster: "Section/ Unit/ Other Offices",
    };
    return (
      <>
        {/* <div className="row border"> */}
        {["Region", "Office", "Division", "Section", "Unit", "Cluster"].map(
          (field, index) => {
            const fieldToLower = field.toLocaleLowerCase();
            return (
              <section className={`${className}`} key={field}>
                <label className="fs-sm">
                  {fieldLabel[field]}{" "}
                  {index === 0 && props.required === true && (
                    <span className="text-danger">*</span>
                  )}
                </label>
                <SearchSelect
                  focus={true}
                  value={this.state[`selected${field}`]?.name}
                  onChange={(value) => this.handleHierarchyChange(field, value)}
                  onClickClear={() => this.handleHierarchyClear(field)} // Use separate clear handler
                  disabled={this.state[fieldToLower].length === 0 || disabled}
                  options={this.state[fieldToLower].map((officeItem) => ({
                    label: officeItem.name,
                    value: {
                      name: officeItem?.name,
                      id: officeItem?.id,
                    },
                  }))}
                  placeholder={`Please Select ${fieldLabel[field]}`}
                  required={index === 0 ? required : false}
                  {...props}
                />
              </section>
            );
          },
        )}
      </>
    );
  }
}
